// askSmart-ui/src/pages/TOSP/portal/LeaseSummaryScreen.tsx
import React from "react";
import { formatCurrency, formatDate } from "../format";
import {
  dateUnderContract,
  formatSpan,
  isOccupancyActive,
  leaseExpiryFor,
  leasedSpaceLabel,
  nextRenewalDate,
  Occupancy,
  rentPsfToday,
  spaceEscalation,
  spanBetween,
  Tenant,
  tenantLabel,
  todayISO,
  Unit,
} from "../types";

/**
 * How long before a renewal date the tenant has to give notice.
 *
 * A flat six months for everyone, deliberately: the lease's own notice period
 * varies clause by clause, and this panel answers "when do I need to decide",
 * which the property manager wants read the same way across the portfolio.
 * The lease-specific figure still drives the admin's expiry flag.
 */
const NOTICE_DAYS = 180;

/**
 * A date shifted by whole days, as ISO text.
 *
 * Parsed as UTC rather than through the local Date constructor: a bare
 * "2031-10-31" read locally is midnight in a western timezone and lands on the
 * 30th once it comes back out.
 */
const shiftDays = (iso: string, days: number): string | null => {
  const at = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(at)) return null;
  return new Date(at + days * 86_400_000).toISOString().slice(0, 10);
};

const dateText = (iso: string | null): string => (iso ? formatDate(iso) : "—");

interface LeaseSummaryScreenProps {
  tenant: Tenant;
  unitById: (unitId: string) => Unit | undefined;
  onBack: () => void;
}

/** One lease's block. Leases are shown separately — they renew separately. */
function LeaseBlock({
  lease,
  unitById,
  heading,
}: {
  lease: Occupancy;
  unitById: (unitId: string) => Unit | undefined;
  heading: string | null;
}) {
  const renewal = nextRenewalDate(lease);
  const noticeBy = renewal ? shiftDays(renewal, -NOTICE_DAYS) : null;
  const usage = (lease.useClauseSummary || "").trim();

  // The same reckoning and the same four colours the admin overview uses —
  // one function, so a tenant and their property manager can never be looking
  // at different answers about how long the lease has left.
  const expiry = leaseExpiryFor(lease, lease.rentRenewalNotations || "");
  const span = expiry.state === "unknown" ? null : spanBetween(todayISO(), expiry.on);

  return (
    <div className="tosp-portal-lease">
      {heading && <div className="tosp-portal-lease-name">{heading}</div>}

      <div className="tosp-portal-lease-head">Important Dates</div>
      {/* The countdown leads, on its own line: it is the answer the other
          three dates are read in order to work out, and it is the one that
          changes every day. */}
      <div className="tosp-portal-lease-row tosp-portal-lease-row-solo">
        <div className="tosp-portal-lease-field">
          <span className="tosp-portal-lease-label">Time Remaining</span>
          {/* The overview's pill, not a boxed value — same shape and same four
              colours, so the tenant and their property manager are looking at
              the same marking. Years and days rather than a bare day count
              ("1,889 days" has to be divided in the head before it means
              anything), and once expired the count runs the other way, which
              the leading + says. */}
          <div>
            {expiry.state === "unknown" || !span ? (
              <span className="tosp-portal-lease-none">—</span>
            ) : (
              <span className={`tosp-portal-lease-flag tosp-portal-lease-${expiry.state}`}>
                {expiry.state === "expired" ? `+${formatSpan(span)}` : formatSpan(span)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Laid out the way the admin's Lease Information reads: the headings on
          one line, each date boxed beneath its own. */}
      <div className="tosp-portal-lease-row">
        <div className="tosp-portal-lease-field">
          <span className="tosp-portal-lease-label">Date Under Contract</span>
          <div className="tosp-portal-lease-value">{dateText(dateUnderContract(lease))}</div>
        </div>

        <div className="tosp-portal-lease-field">
          <span className="tosp-portal-lease-label">Next Renewal Date</span>
          <div className="tosp-portal-lease-value">{dateText(renewal)}</div>
        </div>

        <div className="tosp-portal-lease-field">
          <span className="tosp-portal-lease-label">Renewal Notice by</span>
          {/* Derived, not stored — so it can never drift from the renewal date
              beside it the way a second saved field would. */}
          <div className="tosp-portal-lease-value">{dateText(noticeBy)}</div>
        </div>
      </div>

      <div className="tosp-portal-lease-head">Occupancy</div>
      <table className="tosp-portal-lease-table">
        <thead>
          <tr>
            <th scope="col">Area</th>
            <th scope="col">Sqft</th>
            <th scope="col">Rent psf</th>
          </tr>
        </thead>
        <tbody>
          {lease.leasedSpaces.map((space) => {
            const unit = space.unitId ? unitById(space.unitId) : undefined;
            const psf = rentPsfToday(spaceEscalation(lease, space));
            return (
              <tr key={space.id}>
                <td>{leasedSpaceLabel(space, unit)}</td>
                <td>
                  {unit?.occupancySqFt == null
                    ? "—"
                    : unit.occupancySqFt.toLocaleString()}
                </td>
                {/* rentPsfToday, which is what the admin card labels "Rent PSF
                    Current" — the rate in force TODAY, this year’s step-up
                    counted once it has passed. Not rentPsfCurrent, which despite
                    its name is the year’s PRE-step figure and reads low for
                    every lease that has already stepped up this year. */}
                <td>{psf === null ? "—" : formatCurrency(psf)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="tosp-portal-lease-head">Usage Summary</div>
      <UsageSummary text={usage} />
    </div>
  );
}

/**
 * The use clause, clamped to four lines with a way to see the rest.
 *
 * Some are a sentence and some run half a page, and the clause is the least
 * often read part of this screen — so it is capped rather than allowed to
 * push the dates and areas above it off the top.
 *
 * The control appears only when there is genuinely more to show, measured
 * rather than guessed at: a character count can't know the width it will be
 * wrapped to, and a "Show more" that reveals nothing is worse than none.
 */
function UsageSummary({ text }: { text: string }) {
  const [open, setOpen] = React.useState(false);
  const [clipped, setClipped] = React.useState(false);
  const ref = React.useRef<HTMLParagraphElement>(null);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Only meaningful while the clamp is on — once open, nothing overflows.
    if (!open) setClipped(el.scrollHeight > el.clientHeight + 1);
  }, [text, open]);

  if (!text) {
    return (
      <p className="tosp-portal-lease-usage">
        <span className="tosp-portal-lease-none">Not stated on this lease</span>
      </p>
    );
  }

  return (
    <>
      <p
        ref={ref}
        className={`tosp-portal-lease-usage${open ? " tosp-portal-lease-usage-open" : ""}`}
      >
        {text}
      </p>
      {(clipped || open) && (
        <button
          type="button"
          className="tosp-portal-lease-more"
          onClick={() => setOpen((was) => !was)}
        >
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </>
  );
}

// The lease as the tenant needs it: when it renews, when they have to decide,
// what they hold and what they may use it for.
//
// Its own screen rather than a panel on the dashboard, reached the same way
// Sales and Expenses are: the tiles each state one thing, and this is a
// document rather than a figure.
//
// Read straight off the lease record the admin maintains — nothing here is
// entered or stored twice, so a term corrected on the management side shows
// here without anything being republished.
export default function LeaseSummaryScreen({
  tenant,
  unitById,
  onBack,
}: LeaseSummaryScreenProps) {
  const leases = (tenant.occupancies || []).filter(isOccupancyActive);

  return (
    <div className="tosp-portal">
      <header className="tosp-portal-detail-header">
        <button type="button" className="tosp-portal-back" onClick={onBack}>
          ‹ Dashboard
        </button>
        <h1 className="tosp-portal-detail-title">
          Lease <span className="tosp-portal-detail-who">({tenantLabel(tenant)})</span>
        </h1>
      </header>

      <div className="tosp-portal-lease-panel">
        {leases.length === 0 ? (
          <p className="tosp-portal-lease-none">No active lease on this account.</p>
        ) : (
          leases.map((lease, index) => (
            <LeaseBlock
              key={lease.id}
              lease={lease}
              unitById={unitById}
              // Named only when there is more than one to tell apart, so the
              // ordinary single-lease tenant isn't asked to read a heading
              // that distinguishes nothing.
              heading={leases.length > 1 ? `Lease ${index + 1}` : null}
            />
          ))
        )}
      </div>
    </div>
  );
}
